/**
 * Stat Panel - scroll-triggered count-up animation.
 *
 * When .k-stat-panel__grid enters the viewport, each
 * stat number counts up from 0 to its target value.
 * Staggered per cell for a cascade effect.
 *
 * Respects prefers-reduced-motion - shows final value
 * immediately without animating.
 */
(function (Drupal, once) {
  Drupal.behaviors.kStatPanel = {
    attach(context) {
      const prefersReduced =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      once("k-stat-panel", ".k-stat-panel__grid", context).forEach((grid) => {
        const items = grid.querySelectorAll(".k-stat-panel__item");

        // For each item, find the stat wrapper and extract the
        // numeric value from its text content, preserving the suffix
        const stats = Array.from(items)
          .map((item) => {
            const statEl = item.querySelector(".k-stat-panel__stat");
            const suffixEl = item.querySelector(".k-stat-panel__suffix");
            if (!statEl) return null;

            // Get suffix text to preserve it
            const suffix = suffixEl ? suffixEl.textContent : "";

            // Get the raw text of the stat excluding the suffix element
            // Clone, remove suffix, read remaining text
            const clone = statEl.cloneNode(true);
            const cloneSuffix = clone.querySelector(".k-stat-panel__suffix");
            if (cloneSuffix) cloneSuffix.remove();
            const rawValue = clone.textContent.trim();
            const target = parseFloat(rawValue.replace(/[^0-9.]/g, ""));

            return { statEl, suffixEl, suffix, target, rawValue };
          })
          .filter(Boolean);

        const runCountUp = () => {
          stats.forEach((stat, index) => {
            if (prefersReduced) {
              // Show final value immediately
              return;
            }

            const delay = index * 150; // stagger each cell by 150ms
            const duration = 1200; // ms for full count
            const start = performance.now() + delay;
            const { statEl, suffixEl, suffix, target } = stat;

            // Zero out the number text immediately before animating
            // We rebuild the text node each frame
            const textNode = document.createTextNode("0");

            // Clear existing text nodes but keep suffix element
            Array.from(statEl.childNodes).forEach((node) => {
              if (node.nodeType === Node.TEXT_NODE) {
                statEl.removeChild(node);
              }
            });
            statEl.insertBefore(textNode, statEl.firstChild);

            const tick = (now) => {
              if (now < start) {
                requestAnimationFrame(tick);
                return;
              }

              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);

              // Ease out cubic
              const eased = 1 - Math.pow(1 - progress, 3);
              const current = Math.round(eased * target);

              textNode.textContent = current;

              if (progress < 1) {
                requestAnimationFrame(tick);
              } else {
                // Ensure exact final value
                textNode.textContent =
                  target % 1 === 0 ? target : target.toFixed(1);
              }
            };

            requestAnimationFrame(tick);
          });
        };

        if (prefersReduced) {
          // No animation needed - values already rendered correctly
          return;
        }

        // Screen readers get the real figure, not the count-up: until the
        // grid scrolls into view the visible number reads "0". The
        // animated number is hidden from them and a visually hidden copy
        // of the final value sits beside it.
        stats.forEach(({ statEl, rawValue, suffix }) => {
          const real = document.createElement("span");
          real.className = "visually-hidden";
          real.textContent = rawValue + suffix;
          statEl.setAttribute("aria-hidden", "true");
          statEl.after(real);
        });

        // Zero out all numbers before the grid enters view
        // so there's no flash of the final value
        stats.forEach(({ statEl }) => {
          const textNode = document.createTextNode("0");
          Array.from(statEl.childNodes).forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              statEl.removeChild(node);
            }
          });
          statEl.insertBefore(textNode, statEl.firstChild);
        });

        const observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              runCountUp();
              obs.unobserve(entry.target); // fire once only
            });
          },
          {
            threshold: 0.2,
          },
        );

        observer.observe(grid);
      });
    },
  };
})(Drupal, once);
